/**
 * Test-organization helper.
 *
 * A platform admin can tag an org as an internal test tenant (`is_test_org` on the server). The
 * backend then skips every 402 payment wall for it — compliance entitlement, plan limits and iASM
 * credits — so QA can run the full product in DEV/INTEG/PROD without a subscription.
 *
 * The UI needs the same signal for two reasons:
 *   1. the paywall prompts are mostly REACTIVE to a 402, and those simply stop firing for a test
 *      org, but the billing screens still render "subscribe / upgrade" CTAs that no longer mean
 *      anything for this tenant;
 *   2. a permanently-comped org should be visibly labelled, so nobody mistakes a test tenant for a
 *      real customer (the tag has no expiry — see the server-side AdminOrgResource).
 *
 * The flag rides along on the user profile that login already fetches
 * (`/users/username/{userName}` → `organization.testOrg`), so reading it costs no extra request.
 * Mirrored into localStorage next to `organization`/`orgId`, exactly as session.js does for those.
 *
 * Caveat: the mirror refreshes on login and on any screen that calls {@link rememberTestOrg} with a
 * freshly-fetched user. Tagging an org mid-session shows up on the next such load, not instantly.
 */

const KEY = 'isTestOrg';

/** Mirror `organization.testOrg` from a freshly-fetched user profile into localStorage. */
export function rememberTestOrg(user) {
  try {
    if (user?.organization?.testOrg === true) localStorage.setItem(KEY, 'true');
    else localStorage.removeItem(KEY);
  } catch {
    /* private-mode / storage disabled — fall back to "not a test org" */
  }
}

/** True when the signed-in user's org is tagged as an internal test tenant. */
export function isTestOrg() {
  try {
    return localStorage.getItem(KEY) === 'true';
  } catch {
    return false;
  }
}

/** Drop the mirrored flag (logout). */
export function clearTestOrg() {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* nothing to clear */
  }
}
